import { Divider, Stack } from "@tm/components"
import { useWorkTask, WorkTaskInfo } from "@tm/context-distribution"
import { useDeleteNote, useSaveNote } from "@tm/utils"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { AddCustomPartListRequest, CustomPart, Note, NoteTypes } from "@tm/models"
import {
    EditCustomArticleItemRequest,
    AddCustomArticleItemsRequest,
    useAddCustomArticleItems,
    useDeleteCustomArticles,
    useEditCustomArticleItem,
    useShowCustomArticleItems,
    CustomArticleItemResponse,
} from "@tm/data/basket"
import { useWorkTaskBasketState } from "@bundles/basket"
import { mapCustomArticleItemToCustomPart } from "@bundles/compilations/data/mapper/customItems"
import { CompilationModel } from "@tm/data/compilations"
import { useHistory, useRouteMatch, generatePath } from "react-router"
import { useLocalization } from "@tm/localization"
import { ModuleHeader } from "./ModuleHeader"
import { DialogCustomArticle, EditDialog } from "./EditDialog"
import { ArticleListHandle, OwnArticleList } from "./ArticleList"
import { useOwnArticlesStore } from "./store"

type RouteParams = {
    listId?: string
}

type Props = RouteParams & {
    onClose?(): void
}

export function OwnArticles({ onClose }: Props) {
    const { workTask } = useWorkTask() ?? {}
    if (!workTask) {
        return null
    }
    return <OwnArticlesComponent onClose={onClose} workTask={workTask} />
}

function useOwnArticleCompilations() {
    return useMemo(
        () => ({
            compilations: [
                { id: "1", createDate: new Date(), name: "Test1", description: "Testliste 1", isOwn: false, isPublic: false },
                { id: "2", createDate: new Date(), name: "Test2", description: "Testliste 2", isOwn: false, isPublic: false },
                { id: "3", createDate: new Date(), name: "Test3", description: "Testliste 3", isOwn: false, isPublic: false },
            ],
        }),
        []
    )
}

function OwnArticlesComponent({ onClose, workTask }: Props & { workTask: WorkTaskInfo }) {
    const history = useHistory()
    const { translateText } = useLocalization()
    const [saveError, setSaveError] = useState<string>()
    const { path, params } = useRouteMatch<RouteParams>()
    const { setSelectedCompilation, articleToEdit, searchQuery, setArticleToEdit, setIsSaving } = useOwnArticlesStore()

    const [editNote, setEditNote] = useState<Note>()

    const { basket } = useWorkTaskBasketState(workTask.id)
    const { addCustomParts } = basket.actions

    const { mutateAsync: saveEditedArticle } = useEditCustomArticleItem()
    const { mutateAsync: saveNewArticle } = useAddCustomArticleItems()
    const { mutateAsync: deleteArticles } = useDeleteCustomArticles()

    const { saveNote } = useSaveNote()
    const { deleteNote } = useDeleteNote()

    const listHandle = useRef<ArticleListHandle>(null)

    const listRequest = useMemo<Parameters<typeof useShowCustomArticleItems>[0]>(
        () => ({
            query: searchQuery,
        }),
        [searchQuery]
    )

    const { compilations } = useOwnArticleCompilations()

    const { data: { pages } = {}, fetchNextPage, isLoading, isFetchingNextPage } = useShowCustomArticleItems(listRequest)
    const articles = useMemo(() => pages?.flatMap((x) => x.data) ?? [], [pages])

    useEffect(() => setSelectedCompilation(compilations.find((x) => x.id === params.listId)), [params.listId])

    const handleEditArticle = useCallback((article: CustomArticleItemResponse, note?: Note) => {
        const selectedVatRate = article.vatRates.find((vatRate) => vatRate.isSelected) ?? article.vatRates[0]
        const { id, description, version, articleNumber, categoryId, isDefaultForCategory, purchasePrice, retailPrice } = article
        setArticleToEdit({
            id,
            description,
            version,
            articleNumber,
            categoryId,
            isDefaultForCategory,
            purchasePrice,
            retailPrice,
            selectedVatRate,
            type: "edit",
        })
        setEditNote(note)
    }, [])

    const handleCloseDialog = useCallback(() => {
        setArticleToEdit(undefined)
        setSaveError(undefined)
    }, [])

    const handleDeleteArticles = useCallback(
        async (articlesResponse: CustomArticleItemResponse[]) => {
            await deleteArticles(articlesResponse.map((x) => x.id))
        },
        [deleteArticles]
    )

    const handleAddToBasket = useCallback(
        async (items: [CustomArticleItemResponse, number][]) => {
            const addRequest: AddCustomPartListRequest = {
                customParts: items.map<CustomPart>(([article, quatnity]) => mapCustomArticleItemToCustomPart(article, quatnity, undefined)),
                workTaskId: workTask.id,
                vehicleId: workTask.vehicle?.id,
                customerId: workTask.customer?.id,
            }
            addCustomParts(addRequest)
        },
        [addCustomParts, workTask.customer?.id, workTask.id, workTask.vehicle?.id]
    )

    const handleSaveArticle = useCallback(
        async (article: DialogCustomArticle, note?: string) => {
            try {
                setIsSaving(true)
                setSaveError(undefined)
                if (article.type === "edit") {
                    const request: EditCustomArticleItemRequest = {
                        customArticleItemToEdit: article,
                    }

                    const result = await saveEditedArticle(request)

                    if (result && note !== editNote?.text) {
                        if (note) {
                            const noteRequest: Note = editNote
                                ? { ...editNote, text: note }
                                : { type: NoteTypes.CUSTOM_ARTICLE, text: note, noteId: result.editedCustomArticleItem.id }
                            await saveNote(noteRequest)
                        } else if (editNote) {
                            await deleteNote(editNote.noteId)
                        }
                    }
                } else if (article.description) {
                    const { type, ...customArticleItemToAdd } = article
                    const request: AddCustomArticleItemsRequest = {
                        customArticleItemsToAdd: [customArticleItemToAdd],
                    }
                    const result = await saveNewArticle(request)
                    listHandle.current?.scrollToTop()
                    if (note && result?.addedCustomArticleItems.length === 1) {
                        const addedCustomArticle = result.addedCustomArticleItems[0]
                        const noteRequest: Note = {
                            type: NoteTypes.CUSTOM_ARTICLE,
                            text: note,
                            noteId: addedCustomArticle.id,
                        }
                        await saveNote(noteRequest)
                    }
                }
                handleCloseDialog()
            } catch {
                setSaveError(translateText(13452))
            } finally {
                setIsSaving(false)
            }
        },
        [deleteNote, editNote, handleCloseDialog, saveEditedArticle, saveNewArticle, saveNote]
    )

    const handleSelectCompilation = useCallback(
        (compilation?: CompilationModel) => {
            history.push(generatePath(path, { ...params, listId: compilation?.id }))
        },
        [history, path]
    )

    return (
        <Stack gap={2} flex={1} height="100%" position="relative">
            <ModuleHeader onClose={onClose} />
            <Stack direction="row" gap={2} px={2} divider={<Divider orientation="vertical" />} minHeight={0} height="100%">
                {/* <CompilationList onSelectCompilation={handleSelectCompilation} onExpandCompilationList={() => {}} compilations={compilations} /> */}
                <Stack flex={1} gap={2}>
                    {/* <CompilationDetails />
                    <Divider /> */}
                    <OwnArticleList
                        articles={articles}
                        pageIndex={pages?.length ?? 0}
                        onEditArticle={handleEditArticle}
                        onAddArticlesToBasket={handleAddToBasket}
                        onDeleteArticles={handleDeleteArticles}
                        fetchNextPage={fetchNextPage}
                        isLoading={isLoading || isFetchingNextPage}
                        ref={listHandle}
                    />
                </Stack>
            </Stack>
            {articleToEdit && (
                <EditDialog
                    onClose={handleCloseDialog}
                    onSave={handleSaveArticle}
                    article={articleToEdit}
                    note={editNote?.text}
                    saveError={saveError}
                />
            )}
        </Stack>
    )
}
